import { useState, useEffect, ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { pacientesService, consultasService, type Paciente } from '../services/api';
import { 
  formatarTelefone, 
  formatarCPF, 
  formatarData, 
  formatarMoeda 
} from '../utils/formatters';
import { toast } from 'react-toastify';
import { authService } from '../services/authService';
import FiltroPsicologo from '../components/FiltroPsicologo';

interface PacienteComEstatisticas extends Paciente {
  totalConsultas: number;
  consultasRealizadas: number;
  proximaConsulta?: string;
  ultimaConsulta?: string;
  valorTotalRecebido: number;
  valorPendente: number;
  statusTratamento: 'ativo' | 'pausado' | 'concluido' | 'novo';
}

// Funções auxiliares para status - centralizadas
const getStatusBadgeClass = (status: 'ativo' | 'pausado' | 'concluido' | 'novo'): string => {
  switch (status) {
    case 'ativo': return 'badge-success';
    case 'pausado': return 'badge-warning';
    case 'concluido': return 'badge-info';
    case 'novo': return 'badge-primary';
    default: return 'badge-secondary';
  }
};

const getStatusIcon = (status: 'ativo' | 'pausado' | 'concluido' | 'novo'): string => {
  switch (status) {
    case 'ativo': return 'fa-play';
    case 'pausado': return 'fa-pause';
    case 'concluido': return 'fa-check';
    case 'novo': return 'fa-star';
    default: return 'fa-question';
  }
};

const getStatusLabel = (status: 'ativo' | 'pausado' | 'concluido' | 'novo'): string => {
  switch (status) {
    case 'ativo': return 'Ativo';
    case 'pausado': return 'Pausado';
    case 'concluido': return 'Concluído';
    case 'novo': return 'Novo';
    default: return 'Indefinido';
  }
};

export default function ListaPacientes() {
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [mostrarInativos, setMostrarInativos] = useState(false);
  
  // Estados para filtros e busca
  const [termoBusca, setTermoBusca] = useState('');
  const [filtroServico, setFiltroServico] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [ordenacao, setOrdenacao] = useState<'nome' | 'servico' | 'data'>('nome');
  
  // ✅ MULTI-PSICÓLOGOS: Novos estados
  const [filtroPsicologo, setFiltroPsicologo] = useState<number | null>(null);
  const [modoVisualizacao, setModoVisualizacao] = useState<'todos' | 'meus'>('todos');
  
  // Estados para dados enriquecidos
  const [pacientesEnriquecidos, setPacientesEnriquecidos] = useState<PacienteComEstatisticas[]>([]);
  const [carregandoEstatisticas, setCarregandoEstatisticas] = useState(false);
  const [pacientesSelecionados, setPacientesSelecionados] = useState<Set<string>>(new Set());
  const [modoSelecao, setModoSelecao] = useState(false);

  const navigate = useNavigate();
  const usuarioAtual = authService.getUsuario();
  const isAtendente = usuarioAtual?.cargo === 'atendente';

  const carregarPacientes = async () => {
    try {
      setCarregando(true);
      setErro('');

      // CORREÇÃO CRÍTICA: Verificar autenticação antes de carregar dados
      if (!authService.isAuthenticated()) {
        console.error('[ListaPacientes] Usuário não autenticado - redirecionando');
        authService.logout();
        navigate('/login');
        return;
      }
      
      // DIAGNÓSTICO: Verificar estado da autenticação
      const usuario = authService.getUsuario();
      const empresa = authService.getEmpresa();

      // Validar consistência
      if (!usuario?.cod_empresa || !empresa?.id || usuario.cod_empresa !== empresa.id) {
        console.error('[ListaPacientes] INCONSISTÊNCIA DETECTADA na autenticação:', {
          codEmpresaUsuario: usuario?.cod_empresa,
          idEmpresa: empresa?.id,
          consistente: usuario?.cod_empresa === empresa?.id
        });
        
        alert('Erro de autenticação detectado. Você será redirecionado para o login.');
        authService.logout();
        navigate('/login');
        return;
      }
      
      // ✅ MULTI-PSICÓLOGOS: Carregar dados com filtro
      console.log('[ListaPacientes] Carregando pacientes:', {
        incluirInativos: mostrarInativos,
        filtroPsicologo,
        modoVisualizacao
      });
      
      let data = await pacientesService.listarTodos(mostrarInativos, filtroPsicologo || undefined);
      
      // console.log(`[ListaPacientes] Dados recebidos para empresa ${empresa.id}:`, {
        // totalPacientes: data?.length || 0,
        // pacientesInativos: data?.filter(p => p.ativo === false)?.length || 0,
        // empresaId: empresa.id
      // });
      
      if (!Array.isArray(data)) {
        console.error('Erro: pacienteService.listarTodos() não retornou array', data);
        data = [];
      }
      
      setPacientes(data);
    } catch (error) {
      console.error('[ListaPacientes] Erro ao carregar pacientes:', error);
      
      // Se for erro de autenticação, limpar sessão
      if (error && typeof error === 'object' && 'response' in error && 
          error.response && typeof error.response === 'object' && 'status' in error.response && 
          error.response.status === 401) {
        console.error('[ListaPacientes] Erro 401 - redirecionando para login');
        authService.logout();
        navigate('/login');
        return;
      }
      
      setErro('Falha ao carregar a lista de pacientes. Por favor, tente novamente.');
      setPacientes([]);
    } finally {
      setCarregando(false);
    }
  };
  
  // Carregar dados enriquecidos COM VALIDAÇÃO - OTIMIZADO
  // Usa os pacientes já carregados em vez de fazer nova requisição
  const carregarDadosEnriquecidos = async () => {
    // Se não há pacientes, não há o que enriquecer
    if (pacientes.length === 0) {
      setPacientesEnriquecidos([]);
      return;
    }
    
    try {
      setCarregandoEstatisticas(true);
      
      // VALIDAÇÃO: Verificar se ainda estamos autenticados
      if (!authService.isAuthenticated()) {
        return;
      }
      
      // OTIMIZADO: Usar pacientes do estado e buscar apenas consultas
      const consultasData = await consultasService.listarTodas().catch(() => []);

      // CORREÇÃO: Validação e enriquecimento mais robusto
      const pacientesComStats: PacienteComEstatisticas[] = pacientes.map(paciente => {
        const consultasSeguras = Array.isArray(consultasData) ? consultasData : [];
        const consultasDoPaciente = consultasSeguras.filter(c => c?.pacienteId === paciente.id);
        
        // Filtrar consultas com validação de campos obrigatórios
        const consultasRealizadas = consultasDoPaciente.filter(c => 
          c && typeof c.statusConsulta === 'string' && c.statusConsulta === 'realizada'
        );
        
        const consultasPendentes = consultasDoPaciente.filter(c => 
          c && typeof c.statusConsulta === 'string' && c.statusConsulta === 'agendada'
        );

        // CORREÇÃO: Calcular valores com validação de tipos
        const valorTotalRecebido = consultasRealizadas
          .filter(c => c?.statusPagamento === 'pago' && typeof c.valor === 'number')
          .reduce((total, c) => total + (c.valor || 0), 0);
        
        const valorPendente = consultasRealizadas
          .filter(c => c?.statusPagamento === 'pendente' && typeof c.valor === 'number')
          .reduce((total, c) => total + (c.valor || 0), 0);

        // CORREÇÃO: Determinar próxima e última consulta com validação de data
        const hoje = new Date().toISOString().split('T')[0];
        const proximasConsultas = consultasPendentes
          .filter(c => c?.data && typeof c.data === 'string' && c.data >= hoje)
          .sort((a, b) => (a.data || '').localeCompare(b.data || ''));
        
        const ultimasConsultas = consultasRealizadas
          .filter(c => c?.data && typeof c.data === 'string')
          .sort((a, b) => (b.data || '').localeCompare(a.data || ''));

        // CORREÇÃO: Determinar status do tratamento com lógica mais robusta
        let statusTratamento: 'ativo' | 'pausado' | 'concluido' | 'novo' = 'novo';
        
        if (consultasRealizadas.length === 0 && consultasPendentes.length === 0) {
          statusTratamento = 'novo';
        } else if (consultasPendentes.length > 0) {
          statusTratamento = 'ativo';
        } else if (consultasRealizadas.length > 0) {
          const ultimaData = ultimasConsultas[0]?.data;
          if (ultimaData && typeof ultimaData === 'string') {
            try {
              const diasSemConsulta = Math.floor(
                (new Date().getTime() - new Date(ultimaData).getTime()) / (1000 * 60 * 60 * 24)
              );
              statusTratamento = diasSemConsulta > 30 ? 'pausado' : 'concluido';
            } catch (dateError) {
              statusTratamento = 'concluido';
            }
          }
        }

        // CORREÇÃO: Retornar objeto com todos os campos obrigatórios definidos
        return {
          ...paciente,
          totalConsultas: consultasDoPaciente.length,
          consultasRealizadas: consultasRealizadas.length,
          proximaConsulta: proximasConsultas[0]?.data || undefined,
          ultimaConsulta: ultimasConsultas[0]?.data || undefined,
          valorTotalRecebido,
          valorPendente,
          statusTratamento
        } satisfies PacienteComEstatisticas;
      });

      setPacientesEnriquecidos(pacientesComStats);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      setPacientesEnriquecidos([]);
    } finally {
      setCarregandoEstatisticas(false);
    }
  };

  useEffect(() => {
    carregarPacientes();
  }, [mostrarInativos]); // Recarregar quando mudar a opção de mostrar inativos

  // Carregar dados enriquecidos quando necessário
  useEffect(() => {
    if (pacientes.length > 0) {
      carregarDadosEnriquecidos();
    }
  }, [pacientes, mostrarInativos]);

  // Função para inativar um paciente (antes chamada de excluir)
  const inativarPaciente = async (pacienteId: string, pacienteNome: string) => {
    const confirmacao = window.confirm(`Deseja inativar o paciente ${pacienteNome}? Ele não aparecerá mais nas listagens, mas poderá ser reativado posteriormente.`);
    
    if (!confirmacao) return;
    
    try {
      setCarregando(true);
      await pacientesService.excluir(pacienteId); // Mantém a mesma função que já faz inativação
      
      // Atualizar a lista local
      setPacientes(pacientes.filter(p => p.id !== pacienteId));
      
      toast.success(`Paciente ${pacienteNome} inativado com sucesso`);
    } catch (error) {
      toast.error('Erro ao inativar paciente');
      console.error(error);
    } finally {
      setCarregando(false);
    }
  };

  // Modificar a função reativarPaciente
  const reativarPaciente = async (pacienteId: string, pacienteNome: string) => {
    const confirmacao = window.confirm(`Deseja reativar o paciente ${pacienteNome}?`);
    
    if (!confirmacao) return;
    
    try {
      setCarregando(true);
      // CORREÇÃO: Passar um objeto vazio como segundo argumento
      await pacientesService.reativar(pacienteId, {});
      
      // Recarregar a lista completa após reativação
      await carregarPacientes();
      
      alert(`Paciente ${pacienteNome} reativado com sucesso`); // Usando alert em vez de toast
    } catch (error) {
      console.error('Erro ao reativar paciente:', error);
      alert('Erro ao reativar paciente'); // Usando alert em vez de toast
    } finally {
      setCarregando(false);
    }
  };

  // Nova função para exclusão definitiva - simplificada
  const excluirDefinitivamente = async (pacienteId: string, pacienteNome: string) => {
    // Confirmação única com mensagem clara e enfática
    const confirmacao = window.confirm(
      `⚠️ ATENÇÃO: EXCLUSÃO PERMANENTE ⚠️\n\n` +
      `Você está prestes a EXCLUIR PERMANENTEMENTE o paciente ${pacienteNome}.\n\n` +
      `Esta ação NÃO PODE SER DESFEITA e todos os dados do paciente serão perdidos do sistema.\n\n` +
      `Tem absoluta certeza que deseja continuar?`
    );
    
    if (!confirmacao) return;
    
    try {
      setCarregando(true);
      await pacientesService.excluirDefinitivamente(pacienteId);
      
      // Recarregar a lista após exclusão
      await carregarPacientes();
      
      toast.success(`Paciente excluído permanentemente com sucesso`);
    } catch (error) {
      toast.error('Erro ao excluir paciente permanentemente');
      console.error(error);
    } finally {
      setCarregando(false);
    }
  };

  /**
   * Função segura para obter um paciente enriquecido
   * Sempre retorna um objeto válido, nunca undefined
   */
  const getPacienteEnriquecido = (pacienteId: string): PacienteComEstatisticas => {
    // Valor padrão caso não encontre o paciente enriquecido
    const valorPadrao: PacienteComEstatisticas = {
      id: pacienteId,
      nome: '',
      telefone: '',
      email: '',
      servico: 'psicoterapia',
      ativo: true,
      totalConsultas: 0,
      consultasRealizadas: 0,
      valorTotalRecebido: 0,
      valorPendente: 0,
      statusTratamento: 'novo'
    };
    
    // Buscar o paciente enriquecido na lista
    const pacienteEncontrado = pacientesEnriquecidos.find(p => p.id === pacienteId);
    
    // Retornar o paciente encontrado ou o valor padrão
    return pacienteEncontrado || valorPadrao;
  };

  // CORREÇÃO: Função única de filtrar pacientes
  const pacientesFiltrados = pacientes.filter(paciente => {
    const termoBaixo = termoBusca.toLowerCase();
    const matchBusca = !termoBusca || 
      paciente.nome.toLowerCase().includes(termoBaixo) ||
      paciente.telefone.includes(termoBusca) ||
      paciente.email?.toLowerCase().includes(termoBaixo) ||
      paciente.cpf?.includes(termoBaixo);
    
    const matchServico = !filtroServico || paciente.servico === filtroServico;
    
    // Usar a função segura para verificar o status
    const pacienteEnriquecido = getPacienteEnriquecido(paciente.id);
    const matchStatus = !filtroStatus || pacienteEnriquecido.statusTratamento === filtroStatus;
    
    // ✅ MULTI-PSICÓLOGOS: Filtrar por psicólogo responsável
    const matchPsicologo = !filtroPsicologo || (paciente as any).usuario_responsavel === filtroPsicologo;
    
    return matchBusca && matchServico && matchStatus && matchPsicologo;
  }).sort((a, b) => {
    switch (ordenacao) {
      case 'nome':
        return a.nome.localeCompare(b.nome);
      case 'servico':
        return (a.servico || '').localeCompare(b.servico || '');
      case 'data':
        return (b.createdAt || '').localeCompare(a.createdAt || '');
      default:
        return 0;
    }
  });

  // CORREÇÃO: Funções de exportação e email - declaradas apenas uma vez
  const exportarPacientesSelecionados = () => {
    try {
      const pacientesParaExportar = pacientesFiltrados.filter(p => 
        pacientesSelecionados.has(p.id)
      );
      
      if (pacientesParaExportar.length === 0) {
        alert('Nenhum paciente selecionado para exportação.');
        return;
      }

      const dadosExportacao = pacientesParaExportar.map(paciente => {
        const enriquecido = getPacienteEnriquecido(paciente.id);
        return {
          Nome: paciente.nome,
          Telefone: formatarTelefone(paciente.telefone),
          Email: paciente.email || '',
          Serviço: paciente.servico,
          Status: paciente.ativo === false ? 'Inativo' : 'Ativo',
          'Total Consultas': enriquecido.totalConsultas,
          'Consultas Realizadas': enriquecido.consultasRealizadas,
          'Valor Total Recebido': enriquecido.valorTotalRecebido,
          'Valor Pendente': enriquecido.valorPendente,
          'Status Tratamento': enriquecido.statusTratamento,
          'Próxima Consulta': enriquecido.proximaConsulta ? formatarData(enriquecido.proximaConsulta) : '',
          'Data Cadastro': paciente.createdAt ? formatarData(paciente.createdAt) : ''
        };
      });

      const headers = Object.keys(dadosExportacao[0]);
      const csvContent = [
        headers.join(','),
        ...dadosExportacao.map(row => 
          headers.map(header => {
            const value = row[header as keyof typeof row];
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `pacientes_selecionados_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Erro ao exportar pacientes selecionados:', error);
      alert('Erro ao exportar dados. Tente novamente.');
    }
  };

  const enviarEmailEmMassa = () => {
    try {
      const pacientesComEmail = pacientesFiltrados.filter(p => 
        pacientesSelecionados.has(p.id) && p.email && p.email.trim() !== ''
      );
      
      if (pacientesComEmail.length === 0) {
        alert('Nenhum paciente selecionado possui email cadastrado.');
        return;
      }

      const emails = pacientesComEmail.map(p => p.email).filter(Boolean);
      const emailsUnicos = [...new Set(emails)];

      const assunto = encodeURIComponent('Comunicado da Clínica');
      const corpo = encodeURIComponent(`Prezado(a) paciente,

Este é um comunicado importante da nossa clínica.

Atenciosamente,
Equipe de Atendimento`);

      const mailtoLink = `mailto:?bcc=${emailsUnicos.join(',')}&subject=${assunto}&body=${corpo}`;
      
      if (mailtoLink.length > 2000) {
        const grupoSize = 10;
        const grupos = [];
        for (let i = 0; i < emailsUnicos.length; i += grupoSize) {
          grupos.push(emailsUnicos.slice(i, i + grupoSize));
        }
        
        if (confirm(`A lista é muito grande para envio único. Deseja abrir ${grupos.length} janelas de email (grupos de ${grupoSize})?`)) {
          grupos.forEach((grupo, index) => {
            setTimeout(() => {
              const grupoLink = `mailto:?bcc=${grupo.join(',')}&subject=${assunto}&body=${corpo}`;
              window.open(grupoLink);
            }, index * 1000);
          });
        }
      } else {
        window.open(mailtoLink);
      }

    } catch (error) {
      console.error('Erro ao preparar envio de email em massa:', error);
      alert('Erro ao preparar envio de emails. Tente novamente.');
    }
  };

  const exportarDados = () => {
    try {
      if (pacientes.length === 0) {
        alert('Não há pacientes para exportar.');
        return;
      }

      const dadosCompletos = pacientes.map(paciente => {
        const enriquecido = getPacienteEnriquecido(paciente.id);
        const base = {
          ID: paciente.id,
          Nome: paciente.nome,
          'Data Nascimento': paciente.dataNascimento ? formatarData(paciente.dataNascimento) : '',
          Telefone: formatarTelefone(paciente.telefone),
          Email: paciente.email || '',
          CPF: formatarCPF(paciente.cpf),
          RG: paciente.rg || '',
          Gênero: paciente.genero || '',
          Profissão: paciente.profissao || '',
          Endereço: paciente.endereco || '',
          CEP: paciente.cep || '',
          Cidade: paciente.cidade || '',
          Estado: paciente.estado || '',
          Serviço: paciente.servico,
          Status: paciente.ativo === false ? 'Inativo' : 'Ativo',
          'Data Cadastro': paciente.createdAt ? formatarData(paciente.createdAt) : '',
          'Data Primeira Consulta': paciente.dataPrimeiraConsulta ? formatarData(paciente.dataPrimeiraConsulta) : '',
          'Nome Emergência': paciente.nome_emergencia || '',
          'Telefone Emergência': formatarTelefone(paciente.telefone_emergencia),
          'Parentesco Emergência': paciente.parentesco_emergencia || '',
          'Link Consulta Online': paciente.linkConsultaOnline || '',
          'Total Consultas': enriquecido.totalConsultas,
          'Consultas Realizadas': enriquecido.consultasRealizadas,
          'Valor Total Recebido': enriquecido.valorTotalRecebido,
          'Valor Pendente': enriquecido.valorPendente,
          'Status Tratamento': enriquecido.statusTratamento,
          'Próxima Consulta': enriquecido.proximaConsulta ? formatarData(enriquecido.proximaConsulta) : '',
          'Última Consulta': enriquecido.ultimaConsulta ? formatarData(enriquecido.ultimaConsulta) : ''
        } as any;
        if (!isAtendente) {
          base['Histórico Médico'] = paciente.historicoMedico || '';
          base['Observações'] = paciente.observacoes || '';
        }
        return base;
      });

      const headers = Object.keys(dadosCompletos[0]);
      const csvContent = [
        headers.join(','),
        ...dadosCompletos.map(row => 
          headers.map(header => {
            const value = row[header as keyof typeof row];
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(',')
        )
      ].join('\n');

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `todos_pacientes_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Erro ao exportar todos os dados:', error);
      alert('Erro ao exportar dados. Tente novamente.');
    }
  };

  // CORREÇÃO: Renderização de estatísticas avançadas - declarada apenas uma vez
  const renderEstatisticasAvancadas = () => {
    const total = pacientesEnriquecidos.length;
    const ativos = pacientesEnriquecidos.filter(p => p.ativo !== false).length;
    const novos = pacientesEnriquecidos.filter(p => p.statusTratamento === 'novo').length;
    const emTratamento = pacientesEnriquecidos.filter(p => p.statusTratamento === 'ativo').length;
    const pausados = pacientesEnriquecidos.filter(p => p.statusTratamento === 'pausado').length;
    
    const valorTotalRecebido = pacientesEnriquecidos.reduce((total, p) => total + p.valorTotalRecebido, 0);
    const valorPendente = pacientesEnriquecidos.reduce((total, p) => total + p.valorPendente, 0);

    return (
      <div className="row mb-4">
        <div className="col-lg-3 col-6">
          <div className="small-box bg-info">
            <div className="inner">
              <h3>{total}</h3>
              <p>Total de Pacientes</p>
            </div>
            <div className="icon">
              <i className="fas fa-users"></i>
            </div>
            <Link to="/pacientes/novo" className="small-box-footer">
              Cadastrar novo <i className="fas fa-arrow-circle-right"></i>
            </Link>
          </div>
        </div>

        <div className="col-lg-3 col-6">
          <div className="small-box bg-success">
            <div className="inner">
              <h3>{emTratamento}</h3>
              <p>Em Tratamento Ativo</p>
            </div>
            <div className="icon">
              <i className="fas fa-user-md"></i>
            </div>
            <div className="small-box-footer">
              <span className="text-sm">
                {novos} novos pacientes
              </span>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-6">
          <div className="small-box bg-warning">
            <div className="inner">
              <h3>{pausados}</h3>
              <p>Tratamento Pausado</p>
            </div>
            <div className="icon">
              <i className="fas fa-pause-circle"></i>
            </div>
            <div className="small-box-footer">
              <span className="text-sm">
                Necessitam acompanhamento
              </span>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-6">
          <div className="small-box bg-primary">
            <div className="inner">
              <h3>{formatarMoeda(valorTotalRecebido)}</h3>
              <p>Valor Total Recebido</p>
            </div>
            <div className="icon">
              <i className="fas fa-dollar-sign"></i>
            </div>
            <div className="small-box-footer">
              <span className="text-sm">
                {formatarMoeda(valorPendente)} pendente
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // CORREÇÃO: Renderização de filtros - declarada apenas uma vez
  const renderFiltrosAvancados = () => (
    <div className="card card-outline card-primary">
      <div className="card-header">
        <h3 className="card-title">
          <i className="fas fa-filter mr-2"></i>
          Filtros Avançados
        </h3>
        <div className="card-tools">
          <button type="button" className="btn btn-tool" data-card-widget="collapse">
            <i className="fas fa-minus"></i>
          </button>
        </div>
      </div>
      <div className="card-body">
        <div className="row">
          <div className="col-md-3">
            <div className="form-group">
              <label htmlFor="busca">
                <i className="fas fa-search mr-1"></i>
                Busca Rápida
              </label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  id="busca"
                  placeholder="Nome, telefone, email..."
                  value={termoBusca}
                  onChange={(e) => setTermoBusca(e.target.value)}
                />
                <div className="input-group-append">
                  <span className="input-group-text">
                    <i className="fas fa-search"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-2">
            <div className="form-group">
              <label htmlFor="filtroServico">
                <i className="fas fa-stethoscope mr-1"></i>
                Serviço
              </label>
              <select
                className="form-control"
                id="filtroServico"
                value={filtroServico}
                onChange={(e) => setFiltroServico(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="psicoterapia">Psicoterapia</option>
                <option value="triagem">Triagem</option>
                <option value="avaliacao">Avaliação</option>
                <option value="orientacao">Orientação</option>
                <option value="terapia_casal">Terapia de Casal</option>
              </select>
            </div>
          </div>

          <div className="col-md-2">
            <div className="form-group">
              <label htmlFor="filtroStatus">
                <i className="fas fa-heartbeat mr-1"></i>
                Status Tratamento
              </label>
              <select
                className="form-control"
                id="filtroStatus"
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="novo">Novos</option>
                <option value="ativo">Em Tratamento</option>
                <option value="pausado">Pausados</option>
                <option value="concluido">Concluídos</option>
              </select>
            </div>
          </div>

          {/* ✅ MULTI-PSICÓLOGOS: Filtro por Psicólogo */}
          <div className="col-md-2">
            <FiltroPsicologo
              value={filtroPsicologo}
              onChange={setFiltroPsicologo}
              incluirTodos={true}
              label="Psicólogo"
            />
          </div>
          
          <div className="col-md-2">
            <div className="form-group">
              <label htmlFor="ordenacao">
                <i className="fas fa-sort mr-1"></i>
                Ordenar por
              </label>
              <select
                className="form-control"
                id="ordenacao"
                value={ordenacao}
                onChange={(e) => setOrdenacao(e.target.value as any)}
              >
                <option value="nome">Nome</option>
                <option value="data">Data Cadastro</option>
                <option value="ultima_consulta">Última Consulta</option>
                <option value="proxima_consulta">Próxima Consulta</option>
                <option value="valor_recebido">Valor Recebido</option>
              </select>
            </div>
          </div>

          <div className="col-md-1">
            <div className="form-group">
              <div className="form-label">&nbsp;</div>
              <div className="d-block">
                <button 
                  className={`btn ${mostrarInativos ? 'btn-warning' : 'btn-outline-warning'} btn-block`}
                  onClick={() => setMostrarInativos(!mostrarInativos)}
                  title={mostrarInativos ? 'Ocultar Inativos' : 'Mostrar Inativos'}
                >
                  <i className={`fas ${mostrarInativos ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Ações em massa */}
        <div className="row">
          <div className="col-md-12">
            <div className="btn-toolbar justify-content-between">
              <div className="btn-group">
                <button
                  type="button"
                  className={`btn ${modoSelecao ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => {
                    setModoSelecao(!modoSelecao);
                    setPacientesSelecionados(new Set());
                  }}
                >
                  <i className="fas fa-check-square mr-1"></i>
                  {modoSelecao ? 'Cancelar Seleção' : 'Selecionar Múltiplos'}
                </button>

                {modoSelecao && pacientesSelecionados.size > 0 && (
                  <>
                    <button
                      type="button"
                      className="btn btn-info"
                      onClick={() => exportarPacientesSelecionados()}
                    >
                      <i className="fas fa-download mr-1"></i>
                      Exportar Selecionados ({pacientesSelecionados.size})
                    </button>
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={() => enviarEmailEmMassa()}
                    >
                      <i className="fas fa-envelope mr-1"></i>
                      Enviar E-mail
                    </button>
                  </>
                )}
              </div>

              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => exportarDados()}
                >
                  <i className="fas fa-file-excel mr-1"></i>
                  Exportar Todos
                </button>
                <Link to="/pacientes/novo" className="btn btn-primary">
                  <i className="fas fa-user-plus mr-1"></i>
                  Novo Paciente
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // CORREÇÃO: Renderização em tabela com handlers tipados corretamente
  const renderTabela = () => (
    <div className="card">
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-striped table-hover mb-0">
            <thead className="bg-light">
              <tr>
                {modoSelecao && (
                  <th style={{ width: '50px' }}>
                    <div className="custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="select-all"
                        checked={pacientesFiltrados.length > 0 && pacientesFiltrados.every(p => pacientesSelecionados.has(p.id))}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          const newSelected = new Set(pacientesSelecionados);
                          if (e.target.checked) {
                            pacientesFiltrados.forEach(p => newSelected.add(p.id));
                          } else {
                            pacientesFiltrados.forEach(p => newSelected.delete(p.id));
                          }
                          setPacientesSelecionados(newSelected);
                        }}
                      />
                      <label className="custom-control-label" htmlFor="select-all"></label>
                    </div>
                  </th>
                )}
                <th>Paciente</th>
                <th>Contato</th>
                <th>Serviço</th>
                <th>Status</th>
                <th>Consultas</th>
                <th>Financeiro</th>
                <th style={{ width: '150px' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {pacientesFiltrados.map((paciente) => {
                const enriquecido = getPacienteEnriquecido(paciente.id);
                const isSelected = pacientesSelecionados.has(paciente.id);
                
                return (
                  <tr key={paciente.id} className={paciente.ativo === false ? 'table-secondary' : ''}>
                    {modoSelecao && (
                      <td>
                        <div className="custom-control custom-checkbox">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id={`table-check-${paciente.id}`}
                            checked={isSelected}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              const newSelected = new Set(pacientesSelecionados);
                              if (e.target.checked) {
                                newSelected.add(paciente.id);
                              } else {
                                newSelected.delete(paciente.id);
                              }
                              setPacientesSelecionados(newSelected);
                            }}
                          />
                          <label className="custom-control-label" htmlFor={`table-check-${paciente.id}`}></label>
                        </div>
                      </td>
                    )}
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center mr-3" 
                             style={{ width: '40px', height: '40px', fontSize: '16px', color: 'white' }}>
                          {paciente.nome?.charAt(0).toUpperCase() || 'P'}
                        </div>
                        <div>
                          <strong>{paciente.nome}</strong>
                          {paciente.dataNascimento && (
                            <div><small className="text-muted">{formatarData(paciente.dataNascimento)}</small></div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <i className="fas fa-phone mr-1"></i>
                        {formatarTelefone(paciente.telefone)}
                      </div>
                      {paciente.email && (
                        <div>
                          <i className="fas fa-envelope mr-1"></i>
                          <small>{paciente.email}</small>
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="badge badge-info">
                        {paciente.servico === 'triagem' ? 'Triagem' : 
                         paciente.servico === 'psicoterapia' ? 'Psicoterapia' :
                         paciente.servico}
                      </span>
                    </td>
                    <td>
                      <div>
                        {paciente.ativo === false ? (
                          <span className="badge badge-secondary">Inativo</span>
                        ) : (
                          <span className="badge badge-success">Ativo</span>
                        )}
                      </div>
                      {enriquecido && (
                        <div className="mt-1">
                          <span className={`badge ${getStatusBadgeClass(enriquecido.statusTratamento)}`}>
                            {getStatusLabel(enriquecido.statusTratamento)}
                          </span>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="text-center">
                        <div>
                          <strong>{enriquecido?.totalConsultas || 0}</strong>
                          <small className="text-muted d-block">Total</small>
                        </div>
                        <div className="mt-1">
                          <strong className="text-success">{enriquecido?.consultasRealizadas || 0}</strong>
                          <small className="text-muted d-block">Realizadas</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="text-right">
                        <div className="text-success">
                          <strong>{formatarMoeda(enriquecido?.valorTotalRecebido || 0)}</strong>
                          <small className="text-muted d-block">Recebido</small>
                        </div>
                        {(enriquecido?.valorPendente || 0) > 0 && (
                          <div className="text-warning">
                            <strong>{formatarMoeda(enriquecido.valorPendente)}</strong>
                            <small className="text-muted d-block">Pendente</small>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        {paciente.ativo === false ? (
                          <>
                            <button 
                              onClick={() => reativarPaciente(paciente.id, paciente.nome)}
                              className="btn btn-success"
                              title="Reativar paciente"
                            >
                              <i className="fas fa-undo"></i>
                            </button>
                            
                            {/* Adicionar o botão de exclusão definitiva para pacientes inativos */}
                            <button 
                              onClick={() => excluirDefinitivamente(paciente.id, paciente.nome)}
                              className="btn btn-danger"
                              title="Excluir permanentemente"
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </>
                        ) : (
                          <>
                            <Link 
                              to={`/pacientes/${paciente.id}`} 
                              className="btn btn-info"
                              title="Ver detalhes"
                            >
                              <i className="fas fa-eye"></i>
                            </Link>
                            <Link 
                              to={`/pacientes/${paciente.id}/editar`} 
                              className="btn btn-primary"
                              title="Editar"
                            >
                              <i className="fas fa-edit"></i>
                            </Link>
                            <Link 
                              to={`/consultas/nova?pacienteId=${paciente.id}`} 
                              className="btn btn-success"
                              title="Nova consulta"
                            >
                              <i className="fas fa-plus"></i>
                            </Link>
                            <button 
                              onClick={() => inativarPaciente(paciente.id, paciente.nome)}
                              className="btn btn-danger"
                              title="Inativar paciente"
                            >
                              <i className="fas fa-ban"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {pacientesFiltrados.length === 0 && (
          <div className="text-center p-4">
            <i className="fas fa-search fa-3x text-muted mb-3"></i>
            <p className="text-muted">Nenhum paciente encontrado com os filtros aplicados.</p>
          </div>
        )}
      </div>
    </div>
  );

  // Renderização MOBILE otimizada com cards (Bootstrap 5 + AdminLTE)
  const renderMobileCards = () => (
    <div className="d-md-none">
      {pacientesFiltrados.map((paciente) => {
        const enriquecido = getPacienteEnriquecido(paciente.id);
        const isSelected = pacientesSelecionados.has(paciente.id);
        const statusTratamento = enriquecido.statusTratamento ?? 'novo';
        
        return (
          <div 
            key={paciente.id} 
            className={`card mb-3 shadow-sm ${isSelected ? 'border-primary' : ''} ${paciente.ativo === false ? 'opacity-75' : ''}`}
            style={{ borderRadius: '10px', overflow: 'hidden' }}
          >
            {/* Header */}
            <div className="card-header d-flex justify-content-between align-items-center bg-white border-bottom" 
                 style={{ padding: '12px 16px' }}>
              <div className="d-flex align-items-center gap-2">
                {modoSelecao && (
                  <input
                    type="checkbox"
                    className="form-check-input m-0"
                    checked={isSelected}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const newSelected = new Set(pacientesSelecionados);
                      if (e.target.checked) {
                        newSelected.add(paciente.id);
                      } else {
                        newSelected.delete(paciente.id);
                      }
                      setPacientesSelecionados(newSelected);
                    }}
                    style={{ cursor: 'pointer', width: '22px', height: '22px' }}
                  />
                )}
                <div className="rounded-circle bg-gradient-primary d-flex align-items-center justify-content-center shadow-sm" 
                     style={{ width: '40px', height: '40px', minWidth: '40px', fontSize: '18px', color: 'white', fontWeight: '700' }}>
                  {paciente.nome?.charAt(0).toUpperCase() || 'P'}
                </div>
              </div>
              
              <span className={`badge ${paciente.ativo === false ? 'bg-secondary' : getStatusBadgeClass(statusTratamento)}`}
                    style={{ fontSize: '11px', padding: '6px 10px' }}>
                <i className={`fas ${getStatusIcon(statusTratamento)} me-1`}></i>
                {paciente.ativo === false ? 'Inativo' : getStatusLabel(statusTratamento)}
              </span>
            </div>

            {/* Body */}
            <div className="card-body" style={{ padding: '14px 16px' }}>
              <h5 className="mb-2 fw-bold" style={{ fontSize: '17px', color: '#2c3e50' }}>
                {paciente.nome}
              </h5>
              
              <div className="mb-3" style={{ fontSize: '13px', lineHeight: '1.8' }}>
                <div className="d-flex align-items-start mb-2">
                  <i className="fas fa-stethoscope text-primary me-2 mt-1" style={{ width: '18px', fontSize: '14px' }}></i>
                  <span className="text-muted flex-grow-1">{paciente.servico}</span>
                </div>
                
                <div className="d-flex align-items-start mb-2">
                  <i className="fas fa-phone text-success me-2 mt-1" style={{ width: '18px', fontSize: '14px' }}></i>
                  <a href={`tel:${paciente.telefone}`} className="text-decoration-none text-muted flex-grow-1">
                    {formatarTelefone(paciente.telefone)}
                  </a>
                </div>
                
                {paciente.email && (
                  <div className="d-flex align-items-start mb-2">
                    <i className="fas fa-envelope text-info me-2 mt-1" style={{ width: '18px', fontSize: '14px' }}></i>
                    <a href={`mailto:${paciente.email}`} 
                       className="text-decoration-none text-muted flex-grow-1 text-truncate d-block" 
                       style={{ maxWidth: '230px' }}>
                      {paciente.email}
                    </a>
                  </div>
                )}
              </div>

              {/* Estatísticas em cards */}
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <div className="text-center p-2 rounded" 
                       style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', lineHeight: '1' }}>
                      {enriquecido.totalConsultas}
                    </div>
                    <div style={{ fontSize: '10px', opacity: '0.9', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px' }}>
                      Total
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="text-center p-2 rounded" 
                       style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', lineHeight: '1' }}>
                      {enriquecido.consultasRealizadas}
                    </div>
                    <div style={{ fontSize: '10px', opacity: '0.9', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px' }}>
                      Realizadas
                    </div>
                  </div>
                </div>
              </div>

              {enriquecido.proximaConsulta && (
                <div className="alert alert-info mb-2 py-2 px-3 d-flex align-items-center" 
                     style={{ fontSize: '12px', borderRadius: '8px', border: 'none', background: '#e3f2fd' }}>
                  <i className="fas fa-calendar-check text-info me-2" style={{ fontSize: '16px' }}></i>
                  <div>
                    <strong className="d-block" style={{ fontSize: '11px', color: '#0277bd' }}>Próxima Consulta</strong>
                    <span className="text-dark">{formatarData(enriquecido.proximaConsulta)}</span>
                  </div>
                </div>
              )}

              {enriquecido.valorTotalRecebido > 0 && (
                <div className="d-flex justify-content-between align-items-center p-2 rounded" 
                     style={{ fontSize: '12px', background: '#f8f9fa' }}>
                  <div className="d-flex align-items-center">
                    <i className="fas fa-check-circle text-success me-2"></i>
                    <div>
                      <small className="text-muted d-block" style={{ fontSize: '10px' }}>Recebido</small>
                      <strong className="text-success">{formatarMoeda(enriquecido.valorTotalRecebido)}</strong>
                    </div>
                  </div>
                  {enriquecido.valorPendente > 0 && (
                    <div className="d-flex align-items-center">
                      <i className="fas fa-clock text-warning me-2"></i>
                      <div className="text-end">
                        <small className="text-muted d-block" style={{ fontSize: '10px' }}>Pendente</small>
                        <strong className="text-warning">{formatarMoeda(enriquecido.valorPendente)}</strong>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer com botões touch-friendly (44px min) */}
            <div className="card-footer border-top-0 p-2" style={{ background: '#fafafa' }}>
              <div className="d-flex gap-1">
                {paciente.ativo === false ? (
                  <>
                    <button 
                      onClick={() => reativarPaciente(paciente.id, paciente.nome)}
                      className="btn btn-success btn-sm flex-fill d-flex flex-column align-items-center justify-content-center"
                      style={{ minHeight: '48px', fontSize: '12px', fontWeight: '600' }}
                    >
                      <i className="fas fa-undo mb-1" style={{ fontSize: '16px' }}></i>
                      <span>Reativar</span>
                    </button>
                    {!isAtendente && (
                      <button 
                        onClick={() => excluirDefinitivamente(paciente.id, paciente.nome)}
                        className="btn btn-danger btn-sm flex-fill d-flex flex-column align-items-center justify-content-center"
                        style={{ minHeight: '48px', fontSize: '12px', fontWeight: '600' }}
                      >
                        <i className="fas fa-trash mb-1" style={{ fontSize: '16px' }}></i>
                        <span>Excluir</span>
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <Link 
                      to={`/pacientes/${paciente.id}`} 
                      className="btn btn-info btn-sm flex-fill d-flex flex-column align-items-center justify-content-center text-white"
                      style={{ minHeight: '48px', fontSize: '11px', fontWeight: '600' }}
                    >
                      <i className="fas fa-eye mb-1" style={{ fontSize: '16px' }}></i>
                      <span>Ver</span>
                    </Link>
                    {!isAtendente && (
                      <Link 
                        to={`/pacientes/${paciente.id}/editar`} 
                        className="btn btn-primary btn-sm flex-fill d-flex flex-column align-items-center justify-content-center"
                        style={{ minHeight: '48px', fontSize: '11px', fontWeight: '600' }}
                      >
                        <i className="fas fa-edit mb-1" style={{ fontSize: '16px' }}></i>
                        <span>Editar</span>
                      </Link>
                    )}
                    <Link 
                      to={`/consultas/nova?pacienteId=${paciente.id}`} 
                      className="btn btn-success btn-sm flex-fill d-flex flex-column align-items-center justify-content-center"
                      style={{ minHeight: '48px', fontSize: '11px', fontWeight: '600' }}
                    >
                      <i className="fas fa-calendar-plus mb-1" style={{ fontSize: '16px' }}></i>
                      <span>Consulta</span>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // CORREÇÃO: Implementar renderização em lista com handlers tipados
  const renderLista = () => (
    <div className="card">
      <div className="card-body p-0">
        <div className="list-group list-group-flush">
          {pacientesFiltrados.map((paciente) => {
            const enriquecido = getPacienteEnriquecido(paciente.id);
            const isSelected = pacientesSelecionados.has(paciente.id);
            const statusTratamento = enriquecido.statusTratamento ?? 'novo';
            
            return (
              <div 
                key={paciente.id} 
                className={`list-group-item list-group-item-action ${paciente.ativo === false ? 'bg-light' : ''} ${isSelected ? 'border-primary' : ''}`}
              >
                <div className="d-flex w-100 justify-content-between">
                  <div className="d-flex align-items-center">
                    {modoSelecao && (
                      <div className="custom-control custom-checkbox mr-3">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id={`list-check-${paciente.id}`}
                          checked={isSelected}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            const newSelected = new Set(pacientesSelecionados);
                            if (e.target.checked) {
                              newSelected.add(paciente.id);
                            } else {
                              newSelected.delete(paciente.id);
                            }
                            setPacientesSelecionados(newSelected);
                          }}
                        />
                        <label className="custom-control-label" htmlFor={`list-check-${paciente.id}`}></label>
                      </div>
                    )}
                    
                    <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center mr-3" 
                         style={{ width: '40px', height: '40px', fontSize: '16px', color: 'white' }}>
                      {paciente.nome?.charAt(0).toUpperCase() || 'P'}
                    </div>
                    
                    <div>
                      <h6 className="mb-1">{paciente.nome}</h6>
                      <p className="mb-1 text-muted">
                        <i className="fas fa-stethoscope mr-1"></i>
                        {paciente.servico === 'triagem' ? 'Triagem' : 
                         paciente.servico === 'psicoterapia' ? 'Psicoterapia' :
                         paciente.servico}
                      </p>
                      <small className="text-muted">
                        <i className="fas fa-phone mr-1"></i>
                        {formatarTelefone(paciente.telefone)}
                      </small>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="mb-2">
                      {paciente.ativo === false ? (
                        <span className="badge badge-secondary">Inativo</span>
                      ) : (
                        <span className={`badge ${getStatusBadgeClass(statusTratamento)}`}>
                          {getStatusLabel(statusTratamento)}
                        </span>
                      )}
                    </div>
                    
                    <div className="btn-group btn-group-sm">
                      {paciente.ativo === false ? (
                        <button 
                          onClick={() => reativarPaciente(paciente.id, paciente.nome)}
                          className="btn btn-success btn-sm"
                          title="Reativar paciente"
                        >
                          <i className="fas fa-undo"></i>
                        </button>
                      ) : (
                        <>
                          <Link 
                            to={`/pacientes/${paciente.id}`} 
                            className="btn btn-info btn-sm"
                            title="Ver detalhes"
                          >
                            <i className="fas fa-eye"></i>
                          </Link>
                          <Link 
                            to={`/pacientes/${paciente.id}/editar`} 
                            className="btn btn-primary btn-sm"
                            title="Editar"
                          >
                            <i className="fas fa-edit"></i>
                          </Link>
                          <Link 
                            to={`/consultas/nova?pacienteId=${paciente.id}`} 
                            className="btn btn-success btn-sm"
                            title="Nova consulta"
                          >
                            <i className="fas fa-plus"></i>
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {enriquecido.proximaConsulta && (
                  <div className="mt-2">
                    <small className="text-info">
                      <i className="fas fa-calendar-alt mr-1"></i>
                      <strong>Próxima consulta:</strong> {formatarData(enriquecido.proximaConsulta)}
                    </small>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  if (carregando) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3"></div>
          <p>Carregando pacientes...</p>
        </div>
      </div>
    );
  }
  
  if (erro) {
    return (
      <div className="alert alert-danger">
        <h4 className="alert-heading">
          <i className="fas fa-exclamation-triangle mr-2"></i>
          Erro ao Carregar Dados
        </h4>
        <p>{erro}</p>
        <hr />
        <button className="btn btn-outline-danger" onClick={carregarPacientes}>
          <i className="fas fa-redo mr-2"></i>
          Tentar Novamente
        </button>
      </div>
    );
  }
  
  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0">
                <i className="fas fa-users mr-2"></i>
                Gerenciar Pacientes
              </h1>
              <p className="text-muted">
                Visualize e gerencie todos os pacientes cadastrados no sistema
              </p>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item active">Pacientes</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Estatísticas */}
      {renderEstatisticasAvancadas()}

      {/* Filtros */}
      {renderFiltrosAvancados()}

      {/* Resultados */}
      <div className="row mb-3">
        <div className="col-md-8">
          <h5>
            <i className="fas fa-list mr-2"></i>
            Resultados ({pacientesFiltrados.length} de {pacientes.length})
          </h5>
          {carregandoEstatisticas && (
            <small className="text-muted">
              <i className="fas fa-spinner fa-spin mr-1"></i>
              Carregando estatísticas...
            </small>
          )}
        </div>
        <div className="col-md-4 text-right">
          {modoSelecao && (
            <small className="text-muted">
              {pacientesSelecionados.size} paciente(s) selecionado(s)
            </small>
          )}
        </div>
      </div>

      {/* Conteúdo principal */}
      {pacientesFiltrados.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <div className="mb-4">
              <i className="fas fa-user-slash fa-5x text-muted"></i>
            </div>
            <h4 className="text-muted">Nenhum paciente encontrado</h4>
            <p className="text-muted mb-4">
              {termoBusca || filtroServico 
                ? 'Tente ajustar os filtros de busca ou cadastre um novo paciente.'
                : 'Não há pacientes cadastrados ainda. Comece adicionando um novo paciente.'
              }
            </p>
            <Link to="/pacientes/novo" className="btn btn-primary btn-lg">
              <i className="fas fa-user-plus mr-2"></i>
              Cadastrar Primeiro Paciente
            </Link>
          </div>
        </div>
      ) : (
        <>
          {/* Desktop - Tabela completa */}
          <div className="d-none d-md-block">
            {renderTabela()}
          </div>
          
          {/* Mobile - Cards otimizados */}
          {renderMobileCards()}
        </>
      )}
    </div>
  );
}

